export type PriceChangeScopeValue = 'STORE' | 'SPECIFIC_STORES' | 'ZONE';

export const PRICE_CHANGE_SCOPE = {
    STORE: 'STORE',
    SPECIFIC_STORES: 'SPECIFIC_STORES',
    ZONE: 'ZONE',
} as const satisfies Record<string, PriceChangeScopeValue>;

export const PRICE_CHANGE_SCOPE_LABELS: Record<PriceChangeScopeValue, string> =
    {
        STORE: 'Store',
        SPECIFIC_STORES: 'Specific Stores',
        ZONE: 'Zone',
    };

export const PRICE_CHANGE_SCOPE_DESCRIPTIVE_LABELS: Record<
    PriceChangeScopeValue,
    string
> = {
    STORE: 'Scope: Store',
    SPECIFIC_STORES: 'Scope: Specific Stores',
    ZONE: 'Scope: Zone',
};

function isPriceChangeScopeValue(
    value: unknown,
): value is PriceChangeScopeValue {
    return (
        value === PRICE_CHANGE_SCOPE.STORE ||
        value === PRICE_CHANGE_SCOPE.SPECIFIC_STORES ||
        value === PRICE_CHANGE_SCOPE.ZONE
    );
}

export function getPriceChangeScope(
    request: object | null | undefined,
): PriceChangeScopeValue | null {
    if (!request || !('priceChangeScope' in request)) {
        return null;
    }

    const scope = Reflect.get(request, 'priceChangeScope');
    if (isPriceChangeScopeValue(scope)) {
        return scope;
    }

    return null;
}

export function getSuggestedStoreIds(
    request: object | null | undefined,
): string[] {
    if (!request || !('suggestedStoreIds' in request)) {
        return [];
    }

    const ids = Reflect.get(request, 'suggestedStoreIds');
    if (!Array.isArray(ids)) {
        return [];
    }

    return ids.filter(
        (id): id is string => typeof id === 'string' && id.length > 0,
    );
}

export function getPriceChangeScopeLabel(
    scope: PriceChangeScopeValue | null,
    descriptive = false,
): string | null {
    if (!scope) {
        return null;
    }
    if (descriptive) {
        return PRICE_CHANGE_SCOPE_DESCRIPTIVE_LABELS[scope];
    }
    return PRICE_CHANGE_SCOPE_LABELS[scope];
}

export function resolveSubmittedPriceChangeScope(input: {
    uiScope: PriceChangeScopeValue;
    selectedStoreIds: string[];
    zoneStoreIds: string[];
}): PriceChangeScopeValue {
    if (input.uiScope === PRICE_CHANGE_SCOPE.STORE) {
        return PRICE_CHANGE_SCOPE.STORE;
    }
    if (input.uiScope === PRICE_CHANGE_SCOPE.SPECIFIC_STORES) {
        return PRICE_CHANGE_SCOPE.SPECIFIC_STORES;
    }

    const selectedInZone = input.selectedStoreIds.filter(storeId =>
        input.zoneStoreIds.includes(storeId),
    );
    if (
        selectedInZone.length > 0 &&
        selectedInZone.length === input.zoneStoreIds.length
    ) {
        return PRICE_CHANGE_SCOPE.ZONE;
    }

    return PRICE_CHANGE_SCOPE.SPECIFIC_STORES;
}
